import { useEffect, useMemo } from "react";
import L from "leaflet";
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";

import {
  getDestinationCoordinates,
  getDestinationPort,
  getEta,
  getExactShipCoordinates,
  getGeoJsonRoutePoints,
  getLastEvent,
  getMapPosition,
  getOriginCoordinates,
  getOriginPort,
  getRouteProgress,
  getTrackingNo,
  getTrackingStatus,
  getVesselName,
  getVoyageNo,
} from "../../utils/shipsGoTracking";

const formatDate = (value, withTime = false) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(withTime
      ? {
          hour: "2-digit",
          minute: "2-digit",
        }
      : {}),
  });
};

const pointToArray = (point) => {
  if (!point) {
    return null;
  }

  return [point.latitude, point.longitude];
};

const pointsAreEqual = (firstPoint, secondPoint) => {
  if (!firstPoint || !secondPoint) {
    return false;
  }

  const latitudeDifference = Math.abs(
    Number(firstPoint[0]) - Number(secondPoint[0])
  );

  const longitudeDifference = Math.abs(
    Number(firstPoint[1]) - Number(secondPoint[1])
  );

  return (
    latitudeDifference < 0.000001 &&
    longitudeDifference < 0.000001
  );
};

const removeDuplicatePoints = (points) => {
  return points.filter((point, index, allPoints) => {
    if (!point) {
      return false;
    }

    return !allPoints.some((otherPoint, otherIndex) => {
      return (
        otherIndex < index &&
        pointsAreEqual(point, otherPoint)
      );
    });
  });
};

function FocusOnPosition({
  position,
  fallbackPoints,
  zoom = 5,
}) {
  const map = useMap();

  const positionLatitude = position?.[0] ?? null;
  const positionLongitude = position?.[1] ?? null;

  const firstLatitude =
    fallbackPoints?.[0]?.[0] ?? null;

  const firstLongitude =
    fallbackPoints?.[0]?.[1] ?? null;

  const secondLatitude =
    fallbackPoints?.[1]?.[0] ?? null;

  const secondLongitude =
    fallbackPoints?.[1]?.[1] ?? null;

  useEffect(() => {
    if (
      positionLatitude !== null &&
      positionLongitude !== null
    ) {
      map.flyTo(
        [positionLatitude, positionLongitude],
        zoom,
        {
          animate: true,
          duration: 0.8,
        }
      );

      return;
    }

    const validPoints = [
      firstLatitude !== null &&
      firstLongitude !== null
        ? [firstLatitude, firstLongitude]
        : null,

      secondLatitude !== null &&
      secondLongitude !== null
        ? [secondLatitude, secondLongitude]
        : null,
    ].filter(Boolean);

    if (validPoints.length >= 2) {
      const bounds = L.latLngBounds(validPoints);

      map.fitBounds(bounds.pad(0.18), {
        animate: true,
        duration: 0.8,
      });
    }
  }, [
    map,
    zoom,
    positionLatitude,
    positionLongitude,
    firstLatitude,
    firstLongitude,
    secondLatitude,
    secondLongitude,
  ]);

  return null;
}

const getStatusBadgeClass = (status) => {
  if (status?.group === "delivered") {
    return "bg-emerald-600 text-white";
  }

  if (status?.group === "arrived") {
    return "bg-violet-600 text-white";
  }

  return "bg-blue-600 text-white";
};

const createTrackingIcon = ({
  isExact,
  isAtDestination,
  isDelivered,
}) => {
  const markerClasses = [
    "shipsgo-tracking-marker",

    isAtDestination
      ? "is-at-destination"
      : isExact
      ? "is-exact"
      : "is-fallback",

    isDelivered ? "is-delivered" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const iconSvg = isAtDestination
    ? `
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          d="M4.5 7.5L12 3.5L19.5 7.5V16.5L12 20.5L4.5 16.5V7.5Z"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linejoin="round"
        />

        <path
          d="M4.8 7.7L12 11.7L19.2 7.7"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linejoin="round"
        />

        <path
          d="M12 11.7V20.1"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        />

        <path
          d="M8.2 5.6L15.7 9.6"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          opacity="0.8"
        />
      </svg>
    `
    : `
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          d="M4 15.5L6.1 9H9.1V5.4H14.9V9H17.9L20 15.5L17.6 18.6H6.4L4 15.5Z"
          fill="currentColor"
        />

        <path
          d="M2.8 18.4C3.9 19.3 5 19.7 6.2 19.7C7.4 19.7 8.5 19.3 9.6 18.4C10.7 19.3 11.8 19.7 13 19.7C14.2 19.7 15.3 19.3 16.4 18.4C17.5 19.3 18.6 19.7 19.8 19.7C20.3 19.7 20.7 19.6 21.2 19.5"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
        />
      </svg>
    `;

  return L.divIcon({
    className: "shipsgo-tracking-leaflet-icon",

    html: `
      <div class="${markerClasses}">
        <span class="shipsgo-tracking-pulse"></span>

        <span class="shipsgo-tracking-core">
          ${iconSvg}
        </span>
      </div>
    `,

    iconSize: [54, 54],
    iconAnchor: [27, 27],
    popupAnchor: [0, -28],
  });
};

export default function ShipsGoLiveMap({
  shipment,
  height = 390,
}) {
  const status = getTrackingStatus(shipment);
  const progress = getRouteProgress(shipment);
  const lastEvent = getLastEvent(shipment);

  const exactCoordinates =
    getExactShipCoordinates(shipment);

  const fallbackMapPosition =
    getMapPosition(shipment);

  const originCoordinates =
    getOriginCoordinates(shipment);

  const destinationCoordinates =
    getDestinationCoordinates(shipment);

  /*
   * Arrived, discharged, customs-cleared, released,
   * gate-out and delivered shipments belong at the
   * destination port.
   *
   * This prevents an old vessel coordinate from
   * displaying somewhere in transit after arrival.
   */
  const hasReachedDestination =
    Boolean(destinationCoordinates) &&
    (status.group === "arrived" ||
      status.group === "delivered");

  const displayMapPosition = useMemo(() => {
    if (hasReachedDestination) {
      return {
        ...destinationCoordinates,
        source: "Destination port",
        isExact: false,
        isDestination: true,
      };
    }

    if (exactCoordinates) {
      return {
        ...exactCoordinates,
        isExact: true,
        isDestination: false,
      };
    }

    if (fallbackMapPosition) {
      return {
        ...fallbackMapPosition,
        isExact: false,
        isDestination: false,
      };
    }

    return null;
  }, [
    hasReachedDestination,
    destinationCoordinates,
    exactCoordinates,
    fallbackMapPosition,
  ]);

  const markerPosition =
    pointToArray(displayMapPosition);

  const originPosition =
    pointToArray(originCoordinates);

  const destinationPosition =
    pointToArray(destinationCoordinates);

  const geoJsonRoutePoints =
    getGeoJsonRoutePoints(shipment);

  const routePoints = useMemo(() => {
    let points = [];

    if (geoJsonRoutePoints.length >= 2) {
      points = [...geoJsonRoutePoints];
    } else {
      points = [
        originPosition,
        markerPosition,
        destinationPosition,
      ].filter(Boolean);
    }

    /*
     * Ensure that the route visually reaches the
     * destination when the shipment has arrived.
     */
    if (
      hasReachedDestination &&
      destinationPosition
    ) {
      const lastPoint =
        points[points.length - 1];

      if (
        !pointsAreEqual(
          lastPoint,
          destinationPosition
        )
      ) {
        points.push(destinationPosition);
      }
    } else if (markerPosition) {
      const markerAlreadyIncluded =
        points.some((point) =>
          pointsAreEqual(point, markerPosition)
        );

      if (!markerAlreadyIncluded) {
        points.push(markerPosition);
      }
    }

    return removeDuplicatePoints(points);
  }, [
    geoJsonRoutePoints,
    originPosition,
    markerPosition,
    destinationPosition,
    hasReachedDestination,
  ]);

  const initialCenter =
    markerPosition ||
    originPosition ||
    destinationPosition ||
    [20, 0];

  const focusFallbackPoints = useMemo(
    () => [
      originPosition,
      destinationPosition,
    ],
    [
      originPosition,
      destinationPosition,
    ]
  );

  const isExactPosition =
    Boolean(exactCoordinates) &&
    !hasReachedDestination;

  const markerIcon = useMemo(
    () =>
      createTrackingIcon({
        isExact: isExactPosition,
        isAtDestination:
          hasReachedDestination,
        isDelivered:
          status.group === "delivered",
      }),
    [
      isExactPosition,
      hasReachedDestination,
      status.group,
    ]
  );

  const positionLabel =
    hasReachedDestination
      ? status.group === "delivered"
        ? "Delivered"
        : "Destination port"
      : isExactPosition
      ? "Live ShipsGo position"
      : "Port fallback";

  if (!shipment) {
    return (
      <div
        className="grid place-items-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400"
        style={{ height }}
      >
        Select a shipment to view its ShipsGo location.
      </div>
    );
  }

  if (!markerPosition) {
    return (
      <div
        className="grid place-items-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 text-center dark:border-slate-700 dark:bg-slate-950"
        style={{ height }}
      >
        <div>
          <p className="font-semibold text-slate-800 dark:text-slate-100">
            Position not available yet
          </p>

          <p className="mt-2 max-w-md text-sm leading-6 text-slate-500 dark:text-slate-400">
            ShipsGo has not returned valid latitude and longitude for this
            shipment. Refresh the shipment after the next carrier update.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <style>{`
        .shipsgo-tracking-leaflet-icon {
          background: transparent;
          border: 0;
        }

        .shipsgo-tracking-marker {
          position: relative;
          width: 54px;
          height: 54px;
          display: grid;
          place-items: center;
        }

        .shipsgo-tracking-pulse {
          position: absolute;
          inset: 5px;
          border-radius: 9999px;
          background: rgba(37, 99, 235, 0.25);
          animation: shipsgo-tracking-pulse 1.8s ease-out infinite;
        }

        .shipsgo-tracking-core {
          position: relative;
          z-index: 2;
          width: 40px;
          height: 40px;
          display: grid;
          place-items: center;
          border: 3px solid white;
          border-radius: 9999px;
          color: white;
          background: linear-gradient(
            135deg,
            #2563eb,
            #0891b2
          );
          box-shadow:
            0 10px 24px rgba(37, 99, 235, 0.4),
            0 0 0 1px rgba(37, 99, 235, 0.1);
          animation: shipsgo-tracking-float 2.4s ease-in-out infinite;
        }

        .shipsgo-tracking-marker.is-fallback
          .shipsgo-tracking-pulse {
          background: rgba(245, 158, 11, 0.24);
        }

        .shipsgo-tracking-marker.is-fallback
          .shipsgo-tracking-core {
          background: linear-gradient(
            135deg,
            #d97706,
            #f59e0b
          );
          box-shadow:
            0 10px 24px rgba(245, 158, 11, 0.35),
            0 0 0 1px rgba(245, 158, 11, 0.1);
        }

        .shipsgo-tracking-marker.is-at-destination
          .shipsgo-tracking-pulse {
          background: rgba(124, 58, 237, 0.24);
        }

        .shipsgo-tracking-marker.is-at-destination
          .shipsgo-tracking-core {
          background: linear-gradient(
            135deg,
            #7c3aed,
            #4f46e5
          );
          box-shadow:
            0 10px 26px rgba(124, 58, 237, 0.4),
            0 0 0 1px rgba(124, 58, 237, 0.12);
        }

        .shipsgo-tracking-marker.is-delivered
          .shipsgo-tracking-pulse {
          background: rgba(16, 185, 129, 0.24);
        }

        .shipsgo-tracking-marker.is-delivered
          .shipsgo-tracking-core {
          background: linear-gradient(
            135deg,
            #059669,
            #10b981
          );
          box-shadow:
            0 10px 26px rgba(16, 185, 129, 0.4),
            0 0 0 1px rgba(16, 185, 129, 0.12);
        }

        .shipsgo-tracking-core svg {
          width: 23px;
          height: 23px;
        }

        @keyframes shipsgo-tracking-pulse {
          0% {
            transform: scale(0.65);
            opacity: 0.9;
          }

          100% {
            transform: scale(1.55);
            opacity: 0;
          }
        }

        @keyframes shipsgo-tracking-float {
          0%,
          100% {
            transform: translateY(0);
          }

          50% {
            transform: translateY(-3px);
          }
        }
      `}</style>

      <div className="relative">
        <MapContainer
          center={initialCenter}
          zoom={5}
          scrollWheelZoom
          style={{
            height,
            width: "100%",
          }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          <FocusOnPosition
            position={markerPosition}
            fallbackPoints={
              focusFallbackPoints
            }
            zoom={
              hasReachedDestination ? 6 : 5
            }
          />

          {routePoints.length >= 2 ? (
            <Polyline
              positions={routePoints}
              pathOptions={{
                color:
                  hasReachedDestination
                    ? "#7c3aed"
                    : "#2563eb",

                weight: 4,
                opacity: 0.76,
                dashArray: "10 9",
              }}
            />
          ) : null}

          {originPosition ? (
            <CircleMarker
              center={originPosition}
              radius={7}
              pathOptions={{
                color: "#ffffff",
                weight: 3,
                fillColor: "#0f766e",
                fillOpacity: 1,
              }}
            >
              <Popup>
                <strong>Origin</strong>
                <br />
                {getOriginPort(shipment)}
              </Popup>
            </CircleMarker>
          ) : null}

          {destinationPosition &&
          !hasReachedDestination ? (
            <CircleMarker
              center={destinationPosition}
              radius={7}
              pathOptions={{
                color: "#ffffff",
                weight: 3,
                fillColor: "#16a34a",
                fillOpacity: 1,
              }}
            >
              <Popup>
                <strong>Destination</strong>
                <br />
                {getDestinationPort(
                  shipment
                )}
              </Popup>
            </CircleMarker>
          ) : null}

          <Marker
            position={markerPosition}
            icon={markerIcon}
          >
            <Popup minWidth={225}>
              <div
                style={{
                  lineHeight: 1.55,
                }}
              >
                <strong>
                  {getTrackingNo(shipment)}
                </strong>

                <br />

                Status: {status.label}

                <br />

                Position: {positionLabel}

                <br />

                Vessel:{" "}
                {getVesselName(shipment)}

                <br />

                Voyage:{" "}
                {getVoyageNo(shipment)}

                <br />

                Latitude:{" "}
                {displayMapPosition.latitude.toFixed(
                  6
                )}

                <br />

                Longitude:{" "}
                {displayMapPosition.longitude.toFixed(
                  6
                )}

                {hasReachedDestination ? (
                  <>
                    <br />
                    Destination:{" "}
                    {getDestinationPort(
                      shipment
                    )}
                  </>
                ) : null}
              </div>
            </Popup>
          </Marker>
        </MapContainer>

        {/* Compact information box on the right */}
        <div className="pointer-events-none absolute right-3 top-3 z-[500] w-[210px] max-w-[calc(100%-4.75rem)] rounded-xl border border-white/80 bg-white/95 px-3 py-2.5 shadow-lg backdrop-blur-md dark:border-slate-700 dark:bg-slate-900/95">
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${getStatusBadgeClass(
                status
              )}`}
            >
              {status.label}
            </span>

            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                hasReachedDestination
                  ? status.group ===
                    "delivered"
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                    : "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300"
                  : isExactPosition
                  ? "bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300"
                  : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
              }`}
            >
              {positionLabel}
            </span>
          </div>

          <p className="mt-1.5 truncate text-xs font-bold text-slate-900 dark:text-slate-100">
            {getTrackingNo(shipment)}
          </p>

          <p className="mt-0.5 truncate text-[10px] text-slate-500 dark:text-slate-400">
            {displayMapPosition.latitude.toFixed(
              5
            )}
            ,{" "}
            {displayMapPosition.longitude.toFixed(
              5
            )}
          </p>
        </div>
      </div>

      <div className="grid gap-3 border-t border-slate-100 p-4 dark:border-slate-800 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-900">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Vessel / Voyage
          </p>

          <p className="mt-1 truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
            {getVesselName(shipment)} /{" "}
            {getVoyageNo(shipment)}
          </p>
        </div>

        <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-900">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            ETA
          </p>

          <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">
            {formatDate(getEta(shipment))}
          </p>
        </div>

        <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-900">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Last Event
          </p>

          <p className="mt-1 truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
            {lastEvent.name}
          </p>

          <p className="mt-1 text-xs text-slate-400">
            {formatDate(
              lastEvent.date,
              true
            )}
          </p>
        </div>

        <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-900">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Progress
            </p>

            <span className="text-xs font-bold text-blue-600 dark:text-blue-300">
              {progress}%
            </span>
          </div>

          <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                status.group ===
                "delivered"
                  ? "bg-gradient-to-r from-emerald-600 to-emerald-400"
                  : status.group ===
                    "arrived"
                  ? "bg-gradient-to-r from-violet-600 to-indigo-500"
                  : "bg-gradient-to-r from-blue-600 to-cyan-500"
              }`}
              style={{
                width: `${progress}%`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}